import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Download, RefreshCw, Filter, X, Package, Calendar, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const InternalTransferReport = () => {
  const navigate = useNavigate();
  const { section: sectionSlug } = useParams();
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalBundles, setTotalBundles] = useState(0);
  const [totalQty, setTotalQty] = useState(0);
  const [uniqueTransfers, setUniqueTransfers] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    internal_transfer_number: '',
    bundle_code: '',
    design_name: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(true);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.internal_transfer_number) params.append('internal_transfer_number', filters.internal_transfer_number);
      if (filters.bundle_code) params.append('bundle_code', filters.bundle_code);
      if (filters.design_name) params.append('design_name', filters.design_name);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (sectionSlug) params.append('section', sectionSlug);
      params.append('limit', '10000'); // Increased limit
      
      const response = await apiClient.get(`/sections/dispatch/internal-transfer-report?${params.toString()}`);
      
      if (response.data.success) {
        setReportData(response.data.data || []);
        setTotalBundles(response.data.total_bundles || 0);
        setTotalQty(response.data.total_qty || 0);
        setUniqueTransfers(response.data.unique_transfers || 0);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load internal transfer report');
    } finally {
      setIsLoading(false);
    }
  }, [filters, sectionSlug]);

  useEffect(() => {
    fetchReport();
  }, [sectionSlug]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    fetchReport();
  };

  const handleClearFilters = () => {
    setFilters({ internal_transfer_number: '', bundle_code: '', design_name: '', date_from: '', date_to: '' });
  };

  const handleExportCSV = () => {
    if (reportData.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Internal Transfer Number', 'Bundle Number', 'Design Name', 'Shade', 'Size', 'Qty', 'Transfer Date', 'From Section'];
    const csvContent = [
      headers.join(','),
      ...reportData.map(item => [
        item.internal_transfer_number,
        item.bundle_number,
        `"${item.design_name || ''}"`,
        `"${item.shade || ''}"`,
        `"${item.size || ''}"`,
        item.qty,
        item.transfer_date ? item.transfer_date.split('T')[0] : '',
        `"${item.from_section || ''}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `internal_transfer_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/sections/dispatch')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Internal Transfer Report</h1>
              <p className="text-sm text-[#E8DCC8]">Dispatch Section - Received Transfers</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <ArrowLeftRight className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Transfers</p>
                <p className="text-2xl font-bold" data-testid="total-transfers">{uniqueTransfers}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Package className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Bundles</p>
                <p className="text-2xl font-bold" data-testid="total-bundles">{totalBundles}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Qty (USNs)</p>
                <p className="text-2xl font-bold" data-testid="total-qty">{totalQty}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Hide' : 'Show'}
                </Button>
                <Button
                  onClick={handleSearch}
                  size="sm"
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  disabled={isLoading}
                  data-testid="search-btn"
                >
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Internal Transfer No.</label>
                  <Input
                    value={filters.internal_transfer_number}
                    onChange={(e) => setFilters(prev => ({ ...prev, internal_transfer_number: e.target.value }))}
                    placeholder="IT/..."
                    data-testid="filter-transfer-number"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Bundle Number</label>
                  <Input
                    value={filters.bundle_code}
                    onChange={(e) => setFilters(prev => ({ ...prev, bundle_code: e.target.value }))}
                    placeholder="BUN..."
                    data-testid="filter-bundle-code"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Design Name</label>
                  <Input
                    value={filters.design_name}
                    onChange={(e) => setFilters(prev => ({ ...prev, design_name: e.target.value }))}
                    placeholder="Search design..."
                    data-testid="filter-design"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Date From</label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                    data-testid="filter-date-from"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Date To</label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                    data-testid="filter-date-to"
                  />
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Report Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : reportData.length === 0 ? (
              <div className="text-center py-12">
                <ArrowLeftRight className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No internal transfers found</p>
                <p className="text-sm text-gray-400">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Internal Transfer Number</TableHead>
                      <TableHead className="font-semibold">Bundle Number</TableHead>
                      <TableHead className="font-semibold">Design Name</TableHead>
                      <TableHead className="font-semibold">Shade</TableHead>
                      <TableHead className="font-semibold">Size</TableHead>
                      <TableHead className="font-semibold text-center">Qty</TableHead>
                      <TableHead className="font-semibold">Transfer Date</TableHead>
                      <TableHead className="font-semibold">From Section</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.map((item, index) => (
                      <TableRow key={`${item.internal_transfer_number}-${item.bundle_number}-${index}`} className="hover:bg-gray-50">
                        <TableCell className="font-mono font-bold text-blue-700" data-testid={`transfer-number-${index}`}>
                          {item.internal_transfer_number}
                        </TableCell>
                        <TableCell className="font-mono font-bold text-purple-700" data-testid={`bundle-number-${index}`}>
                          {item.bundle_number}
                        </TableCell>
                        <TableCell className="font-medium" data-testid={`design-name-${index}`}>
                          {item.design_name || '-'}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {item.shade || '-'}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {item.size || '-'}
                        </TableCell>
                        <TableCell className="text-center" data-testid={`qty-${index}`}>
                          <Badge className="bg-green-100 text-green-800">{item.qty}</Badge>
                        </TableCell>
                        <TableCell className="text-gray-600 text-sm">
                          {item.transfer_date ? formatDate(item.transfer_date) : '-'}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {item.from_section || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default InternalTransferReport;
